"use client";

import { useState } from "react";
import Image from "next/image";
import { BedDouble, Users } from "lucide-react";

import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import type { StayRoomCatalogEntry } from "@/lib/stay/roomCatalog";

const formatPrice = (price: number) => `R${Math.round(price).toLocaleString("en-ZA")}`;

const MAX_THUMBS = 6;

/**
 * Guest-facing "Rooms" section for multi-room Stay properties: each room's
 * name, capacity, nightly price and the photos the host assigned to it.
 * Availability lives in exactly one place — the booking form's calendar, with
 * month navigation and greyed-out taken dates. The old per-room 30-night
 * green/grey dot strip was removed (founder decision, 2026-10-27): it could
 * not page months, it duplicated what the booking calendar already tells the
 * guest, and it cluttered the cards. Guests go straight to "Book this room".
 */
export function StayRoomsSection({
  rooms,
}: {
  rooms: StayRoomCatalogEntry[];
}) {
  const [openPhoto, setOpenPhoto] = useState<{ roomName: string; urls: string[]; index: number } | null>(null);

  const withPhotos = rooms.filter((room) => room.photoUrls.length > 0);
  if (withPhotos.length === 0) return null;

  // Send the picked room into the booking form and take the guest there.
  const bookThisRoom = (roomId: string) => {
    window.dispatchEvent(new CustomEvent("zamzi:select-room", { detail: { roomId } }));
    document.getElementById("stay-booking-form")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section className="mt-8" aria-label="Rooms at this property">
      <h2 className="text-xl font-bold">Choose your room</h2>
      <p className="mt-1 text-sm text-muted-foreground">
        Every room books separately with its own calendar and its own price — pick the one that suits your stay.
      </p>
      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        {withPhotos.map((room) => {
          const shown = room.photoUrls.slice(0, MAX_THUMBS);
          const extraCount = room.photoUrls.length - shown.length;
          return (
            <Card key={room.id} className="overflow-hidden">
              <CardContent className="p-0">
                <button
                  type="button"
                  className="group relative block aspect-video w-full"
                  onClick={() => setOpenPhoto({ roomName: room.roomName, urls: room.photoUrls, index: 0 })}
                  aria-label={`View photos of ${room.roomName}`}
                >
                  <Image
                    src={shown[0]}
                    alt={room.roomName}
                    fill
                    sizes="(max-width: 640px) 100vw, 50vw"
                    className="object-cover transition duration-300 group-hover:scale-[1.02]"
                  />
                  {extraCount > 0 && (
                    <span className="absolute bottom-2 right-2 rounded-full bg-black/60 px-2 py-0.5 text-xs font-medium text-white backdrop-blur-sm">
                      +{extraCount} more
                    </span>
                  )}
                </button>
                <div className="p-3">
                  <div className="flex items-baseline justify-between gap-2">
                    <p className="truncate text-sm font-semibold">{room.roomName}</p>
                    {room.pricePerNight !== null && (
                      <p className="shrink-0 text-sm font-semibold text-primary">
                        From {formatPrice(room.pricePerNight)} <span className="font-normal text-muted-foreground">/ night</span>
                      </p>
                    )}
                  </div>
                  {room.sleeps ? (
                    <p className="mt-0.5 flex items-center gap-1 text-xs text-muted-foreground">
                      <Users className="h-3 w-3" /> Sleeps {room.sleeps}
                    </p>
                  ) : null}
                  {shown.length > 1 && (
                    <div className="mt-2 flex gap-1.5">
                      {shown.slice(1).map((url, index) => (
                        <button
                          key={url}
                          type="button"
                          className="relative h-12 w-16 shrink-0 overflow-hidden rounded border border-slate-200"
                          onClick={() => setOpenPhoto({ roomName: room.roomName, urls: room.photoUrls, index: index + 1 })}
                          aria-label={`View photo ${index + 2} of ${room.roomName}`}
                        >
                          <Image src={url} alt={`${room.roomName} photo ${index + 2}`} fill sizes="64px" className="object-cover" />
                        </button>
                      ))}
                    </div>
                  )}
                  <Button size="sm" className="mt-3 w-full" onClick={() => bookThisRoom(room.id)}>
                    Book this room
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Dialog
        open={openPhoto !== null}
        onOpenChange={(next) => {
          if (!next) setOpenPhoto(null);
        }}
      >
        {openPhoto ? (
          <DialogContent className="max-w-4xl p-0">
            <DialogHeader className="px-5 pt-4">
              <DialogTitle className="flex items-center gap-2">
                <BedDouble className="h-4 w-4" />
                {openPhoto.roomName}
              </DialogTitle>
              <DialogDescription>
                Photo {openPhoto.index + 1} of {openPhoto.urls.length}
              </DialogDescription>
            </DialogHeader>
            <div className="relative aspect-[4/3] max-h-[70dvh] w-full bg-black">
              <Image
                src={openPhoto.urls[openPhoto.index]}
                alt={`${openPhoto.roomName} photo ${openPhoto.index + 1}`}
                fill
                sizes="90vw"
                className="object-contain"
              />
            </div>
          </DialogContent>
        ) : null}
      </Dialog>
    </section>
  );
}
